/**
 * API service layer for apps/web.
 * All backend calls go through here so components never hit fetch() directly.
 * Swap the implementation here (mock ↔ real) without touching any component.
 */

const RESERVED_SUBDOMAINS = new Set([
  'api', 'portal', 'www', 'admin', 'app', 'app2', 'cdn', 'assets',
  'static', 'media', 'files', 'docs', 'help', 'support', 'status',
  'blog', 'news', 'mail', 'email', 'ftp', 'sftp', 'ssh', 'vpn',
  'storage', 'storage-console', 'auth', 'ws', 'caddy',
]);

const PLATFORM_HOSTS = new Set([
  'api.mytownspotlight.com',
  'app.mytownspotlight.com',
  'app2.mytownspotlight.com',
  'admin.mytownspotlight.com',
  'admin.mytownspotlight.dev',
  'portal.mytownspotlight.com',
  'portal.mytownspotlight.dev',
  'api.mytownspotlight.dev',
  'auth.mytownspotlight.dev',
  'ws.mytownspotlight.dev',
  'mytownspotlight.dev',
  'mytownspotlight.com',
  'www.mytownspotlight.com',
  'mts.homelab',
  'api.mts.homelab',
  'admin.mts.homelab',
  'portal.mts.homelab',
  'auth.mts.homelab',
  'ws.mts.homelab',
  'caddy.mts.homelab',
  'storage.mts.homelab',
  'storage-console.mts.homelab',
]);

function normalizeHostname(hostname = '') {
  return String(hostname || '').split(':')[0].trim().toLowerCase();
}

export function isPlatformHostname(hostname = window.location.hostname) {
  const normalized = normalizeHostname(hostname);
  if (!normalized) return false;
  if (normalized === 'localhost' || normalized === '127.0.0.1') return true;
  return PLATFORM_HOSTS.has(normalized);
}

export function getApiBaseUrl() {
  // Public browser traffic must stay on the tenant's current origin so the
  // reverse proxy can preserve that hostname for API tenant resolution.
  return '';
}

export function getTenantSlugFromHostname(hostname = window.location.hostname) {
  const normalized = normalizeHostname(hostname);
  if (!normalized || isPlatformHostname(normalized)) return null;

  if (normalized.includes('.api.')) return null;

  const platformTenantSuffixes = [
    '.mts.homelab',
    '.mytownspotlight.dev',
    '.mytownspotlight.com',
  ];

  if (!platformTenantSuffixes.some((suffix) => normalized.endsWith(suffix))) {
    return null;
  }

  const firstPart = normalized.split('.')[0] || null;
  if (!firstPart || RESERVED_SUBDOMAINS.has(firstPart)) return null;
  return firstPart;
}

function readCookie(name) {
  if (typeof document === 'undefined') {
    return null;
  }

  const prefix = `${name}=`;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    if (!cookie.startsWith(prefix)) {
      continue;
    }

    try {
      return decodeURIComponent(cookie.slice(prefix.length));
    } catch {
      return cookie.slice(prefix.length);
    }
  }

  return null;
}

async function request(path, options = {}) {
  const url = `${getApiBaseUrl()}${path}`;
  const tenantSlug = getTenantSlugFromHostname();
  const headers = { ...(options.headers || {}) };

  if (tenantSlug) {
    headers["X-Tenant-Slug"] = tenantSlug;
  }

  const visitorId = readCookie('mts_visitor_id');
  if (visitorId) {
    headers['X-Visitor-Id'] = visitorId;
  }

  const timeoutMs = Number(options.timeoutMs || 0);
  const controller = timeoutMs > 0 ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(new DOMException(`Request timed out after ${timeoutMs}ms`, 'TimeoutError')), timeoutMs)
    : null;

  let response;
  try {
    response = await fetch(url, {
      method: options.method || 'GET',
      headers,
      credentials: options.credentials || 'include',
      cache: options.cache,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal || controller?.signal,
    });
  } catch (cause) {
    const error = new Error(
      cause?.name === 'TimeoutError' || controller?.signal.aborted
        ? 'The tenant service did not respond in time.'
        : 'The tenant service is temporarily unavailable.'
    );
    error.status = 0;
    error.path = path;
    error.cause = cause;
    throw error;
  } finally {
    if (timeout) clearTimeout(timeout);
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const error = new Error(
      payload?.message
      || payload?.error
      || `API error ${response.status}: ${response.statusText} (${path})`
    );
    error.status = response.status;
    error.path = path;
    throw error;
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const bodyPreview = (await response.text()).slice(0, 80).replace(/\s+/g, " ").trim();
    throw new Error(`Expected JSON from ${url}, got ${contentType || "unknown"}. Response starts with: ${bodyPreview}`);
  }

  return response.json();
}

async function requestJson(path, options = {}) {
  return request(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
}

export async function requestPublicJson(path, options = {}) {
  return requestJson(path, options);
}

export function fetchPublicInvoice(token) {
  return requestPublicJson(`/api/public/commerce/invoices/${encodeURIComponent(token)}`);
}

export function fetchPublicListingPlans() {
  return requestPublicJson('/api/public/commerce/listing-plans');
}

export function fetchPublicSalesMaterial(token) {
  return requestPublicJson(`/api/public/sales-materials/${encodeURIComponent(token)}`);
}

export function createPublicInvoiceCheckout(token, paymentOption = 'FULL_BALANCE') {
  return requestPublicJson(`/api/public/commerce/invoices/${encodeURIComponent(token)}/checkout-sessions`, {
    method: 'POST',
    body: { paymentOption },
  });
}

async function requestMultipart(path, formData, options = {}) {
  const url = `${getApiBaseUrl()}${path}`;
  const tenantSlug = getTenantSlugFromHostname();
  const headers = { ...(options.headers || {}) };

  if (tenantSlug) {
    headers["X-Tenant-Slug"] = tenantSlug;
  }

  const response = await fetch(url, {
    method: options.method || 'POST',
    headers,
    credentials: options.credentials || 'include',
    body: formData,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `API error ${response.status}: ${response.statusText} (${path})`);
  }

  return payload;
}

/** Read the bounded public homepage surfaces for the current tenant. */
export async function fetchHomepageListings() {
  return requestPublicJson('/api/public/homepage/listings');
}

/** Fetch all listings for the current tenant */
export async function fetchListings() {
  const data = await request('/api/listings');
  return Array.isArray(data) ? data : (data.listings || []);
}

/** Fetch a single listing by slug */
export async function fetchListingBySlug(slug, options = {}) {
  const params = new URLSearchParams();
  const categorySlug = String(options?.categorySlug || '').trim();

  if (options?.isEmbedPreview) {
    params.set("embed", "listing");
  }

  const queryString = params.toString();
  const path = categorySlug
    ? `/api/listings/${encodeURIComponent(categorySlug)}/${encodeURIComponent(slug)}${queryString ? `?${queryString}` : ""}`
    : `/api/listings/${encodeURIComponent(slug)}${queryString ? `?${queryString}` : ""}`;
  const data = await request(path);
  // API may return { listing: {...} } or the object directly
  return data.listing || data;
}

/** Fetch latest Google review for a listing by slug */
export async function fetchLatestListingReview(slug, options = {}) {
  if (!slug) {
    return null;
  }

  const categorySlug = String(options?.categorySlug || '').trim();
  const path = categorySlug
    ? `/api/listings/${encodeURIComponent(categorySlug)}/${encodeURIComponent(slug)}/reviews/latest`
    : `/api/listings/${encodeURIComponent(slug)}/reviews/latest`;
  const data = await request(path);
  return data?.review || null;
}

/** Update a portal-owned listing from the public listing owner guide. */
export async function updatePortalListing(listingId, patch = {}) {
  if (!listingId) {
    throw new Error('listingId is required');
  }

  return requestJson(`/api/portal/listings/${encodeURIComponent(listingId)}`, {
    method: 'PATCH',
    body: patch,
  });
}

/** Update a portal-owned listing editor section from public inline controls. */
export async function updatePortalListingEditorData(listingId, payload = {}) {
  if (!listingId) {
    throw new Error('listingId is required');
  }

  return requestJson(`/api/portal/listings/${encodeURIComponent(listingId)}/editor-data`, {
    method: 'PATCH',
    body: payload,
  });
}

export function discoverListingSocialProfiles(listingId) {
  return requestJson(`/api/portal/listings/${encodeURIComponent(listingId)}/social-discovery`, { method: 'POST', body: {} });
}

/** Resolve authoritative edit capability for an embedded listing editor. */
export async function fetchPortalListingEditorContract(listingId) {
  if (!listingId) {
    throw new Error('listingId is required');
  }

  return requestJson(`/api/portal/listings/${encodeURIComponent(listingId)}/editor-contract`);
}

/** Update a portal-owned listing's primary and service categories. */
export async function updatePortalListingCategories(listingId, payload = {}) {
  if (!listingId) {
    throw new Error('listingId is required');
  }

  return requestJson(`/api/portal/listings/${encodeURIComponent(listingId)}/categories`, {
    method: 'PATCH',
    body: payload,
  });
}

/** Upload a gallery photo for a portal-owned listing. */
export async function uploadListingGalleryPhoto(listingId, file, options = {}) {
  if (!listingId) {
    throw new Error('listingId is required');
  }
  if (!file) {
    throw new Error('file is required');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('ownerType', 'BUSINESS');
  formData.append('ownerId', String(listingId));
  formData.append('collectionKey', 'listing.gallery');
  formData.append('visibility', String(options.visibility || 'PUBLIC').toUpperCase());
  formData.append('tags', JSON.stringify(['listing', 'gallery', 'upload']));

  if (options.altText) {
    formData.append('altText', String(options.altText));
  }

  return requestMultipart('/api/media/assets', formData, {
    method: 'POST',
  });
}

/** Fetch Google Places photos that can be imported for a listing. */
export async function fetchGooglePlacePhotosForListing(tenantId, googlePlaceId, listingId) {
  if (!tenantId) {
    throw new Error('tenantId is required');
  }
  if (!googlePlaceId) {
    throw new Error('googlePlaceId is required');
  }

  const params = new URLSearchParams();
  if (listingId) {
    params.set('listingId', String(listingId));
  }

  const queryString = params.toString();
  return requestJson(
    `/api/tenants/${encodeURIComponent(tenantId)}/places/${encodeURIComponent(googlePlaceId)}/photos${queryString ? `?${queryString}` : ''}`,
  );
}

/** Import a Google Places photo into a listing gallery. */
export async function importGooglePlacePhotoToListing(tenantId, listingId, photoName) {
  if (!tenantId) {
    throw new Error('tenantId is required');
  }
  if (!listingId) {
    throw new Error('listingId is required');
  }
  if (!photoName) {
    throw new Error('photoName is required');
  }

  return requestJson(`/api/tenants/${encodeURIComponent(tenantId)}/listings/${encodeURIComponent(listingId)}/gallery/import-google-photo`, {
    method: 'POST',
    body: { photoName },
  });
}

/** Fetch all tenant categories */
export async function fetchCategories(selectedCategory) {
  const data = await request(`/api/categories${selectedCategory && selectedCategory !== 'all' ? `?category=${encodeURIComponent(selectedCategory)}` : ''}`);
  return Array.isArray(data) ? data : (data.categories || []);
}

/** Fetch public event categories. */
export async function fetchEventCategories() {
  const data = await request('/api/event-categories');
  return Array.isArray(data) ? data : (data.categories || []);
}

/** Fetch published events for the current tenant. */
export async function fetchEvents(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, value);
  });
  const query = params.toString();
  const data = await request(`/api/events${query ? `?${query}` : ''}`);
  return Array.isArray(data) ? data : (data.events || []);
}

/** Fetch one published event for the current tenant. */
export async function fetchEvent(eventId) {
  const data = await request(`/api/events/${encodeURIComponent(eventId)}`);
  return data?.event || data;
}

/**
 * Fetch current public tenant metadata.
 *
 * Priority:
 * 1) /api/tenants (tenant context from domain/subdomain middleware)
 * 2) /api/tenants/:slug (hostname-derived fallback)
 */
export async function fetchCurrentTenant() {
  const retryDelays = [0, 500, 1500, 3000];
  let tenantList;
  let lastError;

  for (const delayMs of retryDelays) {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    try {
      tenantList = await request('/api/tenants', { cache: 'no-store', timeoutMs: 5000 });
      lastError = null;
      break;
    } catch (error) {
      lastError = error;
      const status = Number(error?.status || 0);
      if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw error;
      }
    }
  }

  if (lastError) throw lastError;

  if (Array.isArray(tenantList) && tenantList.length > 0) {
    return tenantList[0] || null;
  }

  const slug = getTenantSlugFromHostname();
  if (!slug) return null;

  try {
    const tenantBySlug = await request(`/api/tenants/${encodeURIComponent(slug)}`, {
      cache: 'no-store',
      timeoutMs: 5000,
    });
    return tenantBySlug || null;
  } catch (error) {
    if (error?.status === 404) return null;
    throw error;
  }
}

export const communityProgramsApi = {
  publicGet: (slug, params = {}) => request(`/api/community-programs/${encodeURIComponent(slug)}/public?${new URLSearchParams(params)}`),
  get: slug => request(`/api/community-programs/${encodeURIComponent(slug)}`),
  ballot: (slug, params = {}) => request(`/api/community-programs/${encodeURIComponent(slug)}/ballot?${new URLSearchParams(params)}`),
  publicNominee: nomineeId => request(`/api/community-program-nominees/${encodeURIComponent(nomineeId)}/public`),
  nominee: nomineeId => request(`/api/community-program-nominees/${encodeURIComponent(nomineeId)}`),
  vote: (nomineeId, confirmChange = false) => request(`/api/community-program-nominees/${encodeURIComponent(nomineeId)}/vote`, { method:'POST', headers:{'Content-Type':'application/json'}, body:{confirmChange} }),
  nominateManual: (categoryId, data) => request(`/api/community-program-categories/${encodeURIComponent(categoryId)}/nominate`, { method:'POST', headers:{'Content-Type':'application/json'}, body:data }),
};

export function fetchListingRelatedContent(listingId) {
  return request(`/api/listings/${encodeURIComponent(listingId)}/related-content`);
}
